import { useState } from 'react';
import { Copy, CheckCircle, FileText } from 'lucide-react';
import type { RepairLogEntry } from '../reconciliation-types';
import type { DiagnosticStats } from '../../invoice-repair/types';

interface AccountingNoteProps {
  repairLog: RepairLogEntry[];
  dateFrom: string;
  dateTo: string;
  stats: DiagnosticStats | null;
}

export function AccountingNote({ repairLog, dateFrom, dateTo, stats }: AccountingNoteProps) {
  const [copied, setCopied] = useState(false);

  const monthLabel = new Date(dateFrom).toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });

  const generateNote = () => {
    const lines: string[] = [];
    lines.push(`NOTATKA KSIĘGOWA — Uzgadnianie faktur za ${monthLabel}`);
    lines.push(`Okres: ${dateFrom} — ${dateTo}`);
    lines.push(`Data uzgodnienia: ${new Date().toLocaleDateString('pl-PL')}`);
    lines.push('');

    if (stats) {
      lines.push('PODSUMOWANIE:');
      lines.push(`  Zamówienia w okresie: ${stats.totalOrders}`);
      lines.push(`  Zamówienia z fakturą: ${stats.ordersWithInvoice}`);
      lines.push(`  Faktury VAT (Fakturownia): ${stats.fakturowniaVatCount}`);
      lines.push(`  Korekty (Fakturownia): ${stats.fakturowniaCorrectionsCount}`);
      lines.push(`  Faktury VAT (EcomFlow): ${stats.ecomflowInvoiceCount}`);
      lines.push('');
    }

    if (repairLog.length > 0) {
      lines.push('WYKONANE OPERACJE:');
      repairLog.forEach((entry, i) => {
        lines.push(`  ${i + 1}. [${entry.result.toUpperCase()}] ${entry.description}`);
        lines.push(`     Czas: ${entry.timestamp.toLocaleString('pl-PL')}`);
        if (entry.changes.length > 0) {
          entry.changes.slice(0, 5).forEach(c => {
            lines.push(`     - ${c.field}: ${c.oldValue} → ${c.newValue}`);
          });
          if (entry.changes.length > 5) {
            lines.push(`     ...i ${entry.changes.length - 5} więcej zmian`);
          }
        }
      });
    } else {
      lines.push('Brak wykonanych operacji naprawczych.');
    }

    return lines.join('\n');
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(generateNote());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-xl border border-border-subtle bg-surface-raised/30 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-text-secondary flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Notatka księgowa
        </h4>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-surface-hover border border-border text-xs text-text-secondary hover:text-accent-primary hover:border-accent-primary/30 transition-all"
        >
          {copied ? <CheckCircle className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Skopiowano' : 'Kopiuj'}
        </button>
      </div>
      <pre className="text-[11px] text-text-secondary bg-surface-raised rounded-lg p-3 overflow-x-auto whitespace-pre-wrap max-h-60 overflow-y-auto font-mono">
        {generateNote()}
      </pre>
    </div>
  );
}
